import React, { useState, useEffect } from 'react';
import DataTable from 'react-data-table-component';
import {
    Edit2,
    Trash2,
    Search,
    UserPlus,
    Download,
    X,
    Mail,
    User,
    UserCircle,
    ShieldCheck
} from 'lucide-react';
import SidebarAdmin from './superdashboard/SideBarAdmin';
import MobileNavigationAdmin from './superdashboard/MobileNavigationAdmin';
import DashboardHeaderAdmin from './superdashboard/DashboardHeaderAdmin';
import AdminMenu from '../components/AdminMenu';

const Compte = () => {
    const [isSidebarOpen, setIsSidebarOpen] = useState(true);
    const [filterText, setFilterText] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [formData, setFormData] = useState({
        prenom: '',
        nom: '',
        email: '',
        role: 'Utilisateur',
        telephone: '',
        password: '',
        confirmPassword: ''
    });
    const [errors, setErrors] = useState({});

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.prenom.trim()) newErrors.prenom = 'Le prénom est requis';
        if (!formData.nom.trim()) newErrors.nom = 'Le nom est requis';
        if (!formData.email.trim()) {
            newErrors.email = 'L\'email est requis';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = 'Email invalide';
        }
        if (!formData.password) {
            newErrors.password = 'Le mot de passe est requis';
        } else if (formData.password.length < 6) {
            newErrors.password = 'Le mot de passe doit contenir au moins 6 caractères';
        }
        if (formData.password !== formData.confirmPassword) {
            newErrors.confirmPassword = 'Les mots de passe ne correspondent pas';
        }
        return newErrors;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const validationErrors = validateForm();
        if (Object.keys(validationErrors).length === 0) {
            // Submit form
            console.log('Form submitted:', formData);
            setShowModal(false);
            setFormData({
                prenom: '',
                nom: '',
                email: '',
                role: 'Utilisateur',
                telephone: '',
                password: '',
                confirmPassword: ''
            });
        } else {
            setErrors(validationErrors);
        }
    };

    const users = [
        {
            id: 1,
            nom: 'Amadou',
            prenom: 'Ibrahim',
            email: 'amadou.ibrahim@email.com',
            role: 'Admin',
            status: 'Actif',
            dateCreation: '2024-03-15'
        },
        {
            id: 2,
            nom: 'Mariama',
            prenom: 'Diallo',
            email: 'mariama.diallo@email.com',
            role: 'Utilisateur',
            status: 'Inactif',
            dateCreation: '2024-03-14'
        },
    ];

    const columns = [
        {
            name: 'Nom',
            selector: row => `${row.prenom} ${row.nom}`,
            sortable: true,
        },
        {
            name: 'Email',
            selector: row => row.email,
            sortable: true,
        },
        {
            name: 'Rôle',
            selector: row => row.role,
            sortable: true,
        },
        {
            name: 'Statut',
            selector: row => row.status,
            cell: row => (
                <span className={`px-2 py-1 text-xs rounded-full ${row.status === 'Actif'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                    {row.status}
                </span>
            ),
            sortable: true,
        },
        {
            name: 'Date de création',
            selector: row => row.dateCreation,
            sortable: true,
        },
        {
            name: 'Actions',
            cell: row => (
                <div className="flex gap-2">
                    <button className="p-1 hover:bg-gray-100 rounded">
                        <Edit2 className="w-4 h-4 text-blue-600" />
                    </button>
                    <button className="p-1 hover:bg-gray-100 rounded">
                        <Trash2 className="w-4 h-4 text-red-600" />
                    </button>
                </div>
            ),
        },
    ];

    const Modal = ({ isOpen, onClose, children }) => {
        if (!isOpen) return null;

        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
                <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4">
                    <div className="flex justify-between items-center mb-6">
                        <div>
                            <h2 className="text-2xl font-bold text-gray-900">Ajouter un utilisateur</h2>
                            <p className="text-sm text-gray-500 mt-1">
                                Remplissez les informations pour créer un nouveau compte utilisateur
                            </p>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                        >
                            <X className="w-6 h-6 text-gray-500" />
                        </button>
                    </div>
                    {children}
                </div>
            </div>
        );
    };

    const filteredItems = users.filter(
        item => {
            const searchText = filterText.toLowerCase();
            return (
                item.nom.toLowerCase().includes(searchText) ||
                item.prenom.toLowerCase().includes(searchText) ||
                item.email.toLowerCase().includes(searchText)
            );
        }
    );

    const exportToCSV = () => {
        const csvContent = [
            ["Nom", "Email", "Rôle", "Statut", "Date de création"],
            ...users.map(user => [
                `${user.prenom} ${user.nom}`,
                user.email,
                user.role,
                user.status,
                user.dateCreation
            ])
        ].map(e => e.join(",")).join("\n");

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute("download", "users.csv");
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const customStyles = {
        table: {
            style: {
                backgroundColor: 'white',
                borderRadius: '0.5rem',
            },
        },
        headRow: {
            style: {
                backgroundColor: '#f9fafb',
                borderBottomWidth: '1px',
                borderBottomColor: '#e5e7eb',
                minHeight: '3.5rem',
            },
        },
        rows: {
            style: {
                minHeight: '3.5rem',
                fontSize: '0.875rem',
                '&:hover': {
                    backgroundColor: '#f9fafb',
                    cursor: 'pointer',
                },
            },
        },
        pagination: {
            style: {
                borderTopWidth: '1px',
                borderTopColor: '#e5e7eb',
            },
            pageButtonsStyle: {
                borderRadius: '0.375rem',
                height: '2rem',
                width: '2rem',
                padding: '0.25rem',
                margin: '0 0.25rem',
                cursor: 'pointer',
                transition: '0.2s',
                color: '#437011',
                fill: '#437011',
                '&:disabled': {
                    cursor: 'unset',
                    color: '#cbd5e1',
                    fill: '#cbd5e1',
                },
            },
        },
    };

    return (
        <div className="flex min-h-screen bg-[#F4F8E9]">
            <SidebarAdmin
                isOpen={isSidebarOpen}
                menuItems={AdminMenu}
                onClose={() => setIsSidebarOpen(false)}
            />

            <div className="flex-1">
                <DashboardHeaderAdmin
                    isSidebarOpen={isSidebarOpen}
                    setIsSidebarOpen={setIsSidebarOpen}
                />

                <main className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-8 pb-20 lg:pb-8">
                    {/* Header Section */}
                    <div className="flex justify-between items-center mb-6">
                        <div>
                            <h1 className="text-2xl font-bold text-[#437011]">
                                Gestion des Comptes
                            </h1>
                            <p className="text-gray-600">
                                Gérez les utilisateurs de la plateforme
                            </p>
                        </div>
                        <div className="flex gap-2">
                            <button 
                                onClick={exportToCSV}
                                className="border border-[#437011] text-[#437011] hover:bg-[#437011] hover:text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
                            >
                                <Download className="w-4 h-4" />
                                Export CSV
                            </button>
                            <button 
                                onClick={() => setShowModal(true)}
                                className="bg-[#437011] hover:bg-[#2F5000] text-white px-4 py-2 rounded-lg flex items-center gap-2"
                            >
                                <UserPlus className="w-4 h-4" />
                                Ajouter
                            </button>
                        </div>
                    </div>

                    {/* Search and DataTable */}
                    <div className="bg-white rounded-lg shadow p-4">
                        <div className="mb-4 w-72">
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                                <input
                                    type="text"
                                    placeholder="Rechercher..."
                                    className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#437011]"
                                    value={filterText}
                                    onChange={e => setFilterText(e.target.value)}
                                />
                            </div>
                        </div>

                        <DataTable
                            columns={columns}
                            data={filteredItems}
                            pagination
                            paginationPerPage={10}
                            customStyles={customStyles}
                            highlightOnHover
                            pointerOnHover
                        />
                    </div>
                </main>

                <MobileNavigationAdmin menuItems={AdminMenu} />

                {/* Modal d'ajout d'utilisateur */}
                <Modal isOpen={showModal} onClose={() => setShowModal(false)}>
                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Prénom */}
                            <div>
                                <div className="relative">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Prénom
                                    </label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <User className="h-5 w-5 text-gray-400" />
                                        </div>
                                        <input 
                                            type="text"
                                            name="prenom"
                                            value={formData.prenom}
                                            onChange={handleInputChange}
                                            className={`pl-10 w-full rounded-lg border ${errors.prenom ? 'border-red-500' : 'border-gray-300'} p-2.5 focus:ring-2 focus:ring-[#437011]`}
                                            placeholder="Entrez le prénom"
                                        />
                                    </div>
                                    {errors.prenom && <p className="mt-1 text-sm text-red-500">{errors.prenom}</p>}
                                </div>
                            </div>

                            {/* Nom */}
                            <div>
                                <div className="relative">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Nom
                                    </label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <UserCircle className="h-5 w-5 text-gray-400" />
                                        </div>
                                        <input 
                                            type="text"
                                            name="nom"
                                            value={formData.nom}
                                            onChange={handleInputChange}
                                            className={`pl-10 w-full rounded-lg border ${errors.nom ? 'border-red-500' : 'border-gray-300'} p-2.5 focus:ring-2 focus:ring-[#437011]`}
                                            placeholder="Entrez le nom"
                                        />
                                    </div>
                                    {errors.nom && <p className="mt-1 text-sm text-red-500">{errors.nom}</p>}
                                </div>
                            </div>

                            {/* Email */}
                            <div>
                                <div className="relative">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Email
                                    </label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <Mail className="h-5 w-5 text-gray-400" />
                                        </div>
                                        <input 
                                            type="email"
                                            name="email"
                                            value={formData.email}
                                            onChange={handleInputChange}
                                            className={`pl-10 w-full rounded-lg border ${errors.email ? 'border-red-500' : 'border-gray-300'} p-2.5 focus:ring-2 focus:ring-[#437011]`}
                                            placeholder="exemple@email.com"
                                        />
                                    </div>
                                    {errors.email && <p className="mt-1 text-sm text-red-500">{errors.email}</p>}
                                </div>
                            </div>

                            {/* Téléphone */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Téléphone
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                        </svg>
                                    </div>
                                    <input 
                                        type="tel"
                                        name="telephone"
                                        value={formData.telephone}
                                        onChange={handleInputChange}
                                        className="pl-10 w-full rounded-lg border border-gray-300 p-2.5 focus:ring-2 focus:ring-[#437011]"
                                        placeholder="+227 XX XX XX XX"
                                    />
                                </div>
                            </div>

                            {/* Rôle */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Rôle
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <ShieldCheck className="h-5 w-5 text-gray-400" />
                                    </div>
                                    <select 
                                        name="role"
                                        value={formData.role}
                                        onChange={handleInputChange}
                                        className="pl-10 w-full rounded-lg border border-gray-300 p-2.5 focus:ring-2 focus:ring-[#437011] appearance-none bg-white"
                                    >
                                        <option value="Utilisateur">Utilisateur</option>
                                        <option value="Admin">Admin</option>
                                        <option value="Modérateur">Modérateur</option>
                                    </select>
                                </div>
                            </div>

                            {/* Mot de passe */}
                            <div>
                                <div className="relative">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Mot de passe
                                    </label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                            </svg>
                                        </div>
                                        <input 
                                            type="password"
                                            name="password"
                                            value={formData.password}
                                            onChange={handleInputChange}
                                            className={`pl-10 w-full rounded-lg border ${errors.password ? 'border-red-500' : 'border-gray-300'} p-2.5 focus:ring-2 focus:ring-[#437011]`}
                                            placeholder="Entrez le mot de passe"
                                        />
                                    </div>
                                    {errors.password && <p className="mt-1 text-sm text-red-500">{errors.password}</p>}
                                </div>
                            </div>

                            {/* Confirmer le mot de passe */}
                            <div>
                                <div className="relative">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Confirmer le mot de passe
                                    </label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                            </svg>
                                        </div>
                                        <input 
                                            type="password"
                                            name="confirmPassword"
                                            value={formData.confirmPassword}
                                            onChange={handleInputChange}
                                            className={`pl-10 w-full rounded-lg border ${errors.confirmPassword ? 'border-red-500' : 'border-gray-300'} p-2.5 focus:ring-2 focus:ring-[#437011]`}
                                            placeholder="Confirmez le mot de passe"
                                        />
                                    </div>
                                    {errors.confirmPassword && <p className="mt-1 text-sm text-red-500">{errors.confirmPassword}</p>}
                                </div>
                            </div>
                        </div>

                        {/* Boutons */}
                        <div className="flex justify-end gap-2 mt-6 pt-4 border-t">
                            <button 
                                type="button" 
                                onClick={() => {
                                    setShowModal(false);
                                    setFormData({
                                        prenom: '',
                                        nom: '',
                                        email: '',
                                        role: 'Utilisateur',
                                        telephone: '',
                                        password: '',
                                        confirmPassword: ''
                                    });
                                    setErrors({});
                                }}
                                className="px-4 py-2 border rounded-md hover:bg-gray-50 transition-colors"
                            >
                                Annuler
                            </button>
                            <button 
                                type="submit" 
                                className="px-4 py-2 bg-[#437011] text-white rounded-md hover:bg-[#2F5000] transition-colors flex items-center gap-2"
                            >
                                <UserPlus className="w-4 h-4" />
                                Ajouter
                            </button>
                        </div>
                    </form>
                </Modal>
            </div>
        </div>
    );
};

export default Compte;